import React from "react";
import { Order, CheckPromoCodeResponse } from "../features/orders/types";
import { useQuery } from "@apollo/client/react";
import { CHECK_PROMO_CODE_QUERY } from "../features/orders/mutations";

type ParsedGarment = {
  name?: string | number;
  qty?: string | number;
  type?: string | number;
  quantity?: string | number;
  cleaning_types?: string;
};

const parseGarmentDetails = (value: string): ParsedGarment[] => {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    try {
      const normalized = value
        .replace(/([{,]\s*)([A-Za-z_][A-Za-z0-9_]*)\s*:/g, '$1"$2":')
        .replace(/'/g, '"');
      const parsed = JSON.parse(normalized);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
};

const formatGarmentSummary = (value: string): string[] => {
  const garments = parseGarmentDetails(value);
  if (garments.length === 0) return [];

  return garments
    .map((garment) => {
      const itemName = (garment.name ?? garment.type ?? "Item")
        .toString()
        .replace(/\b\w/g, (ch) => ch.toUpperCase());
      const rawQty = garment.qty ?? garment.quantity;
      const numericQty = Number(rawQty);
      const qty =
        Number.isFinite(numericQty) && numericQty > 0 ? numericQty : 1;
      return `${qty} x ${itemName}`;
    })
    .filter((line) => line.trim().length > 0);
};

const getStatusColor = (status: string) => {
  const s = status?.toLowerCase();
  if (s === "delivered") return "text-green-600 bg-green-50";
  if (s === "processing" || s === "collected") return "text-blue-600 bg-blue-50";
  if (s === "open" || s === "received") return "text-yellow-600 bg-yellow-50";
  if (s === "ready_for_delivery" || s === "ready_to_pickup")
    return "text-purple-600 bg-purple-50";
  return "text-gray-600 bg-gray-100";
};

interface OrderDetailsModalProps {
  open: boolean;
  onClose: () => void;
  order: Order | null;
  onDownloadInvoice?: () => void;
}

const parseGarmentSummary = (
  value: string,
): {
  lines: string[];
  total: number;
} => {
  const garments = parseGarmentDetails(value);

  if (garments.length === 0) {
    return {
      lines: [],
      total: 0,
    };
  }

  let total = 0;

  const lines = garments.flatMap((garment) => {
    const itemName = (garment.name ?? garment.type ?? "Item")
      .toString()
      .replace(/\b\w/g, (ch) => ch.toUpperCase());

    if (!garment.cleaning_types) {
      return [`1 x ${itemName}`];
    }

    try {
      const normalized = garment.cleaning_types.replace(/'/g, '"');

      const cleaningTypes = JSON.parse(normalized);

      if (!Array.isArray(cleaningTypes)) {
        return [`1 x ${itemName}`];
      }

      return cleaningTypes.map((type: any) => {
        const serviceTitle = type.title || "Service";

        const amount = Number(type.price) || 0;

        total += amount;

        return `1 x ${itemName} - ${serviceTitle} - ${amount} QAR`;
      });
    } catch {
      return [`1 x ${itemName}`];
    }
  });

  return {
    lines,
    total,
  };
};

export const OrderDetailsModal: React.FC<OrderDetailsModalProps> = ({
  open,
  onClose,
  order,
  onDownloadInvoice,
}) => {
  const garmentDetailsValue =
    typeof order?.garment_details === "string"
      ? order.garment_details
      : JSON.stringify(order?.garment_details ?? []);

  const { lines: garmentLines, total: calculatedTotal } =
    parseGarmentSummary(garmentDetailsValue);

  const finalAmount =
    Number(order?.amount) > 0 ? Number(order?.amount) : Number(calculatedTotal);

  const { data: promoData } = useQuery<CheckPromoCodeResponse>(
    CHECK_PROMO_CODE_QUERY,
    {
      variables: {
        code: order?.promo_code,
      },
      skip: !order?.promo_code,
    },
  );

  const promo = promoData?.checkPromoCode?.[0];

  let discountAmount = 0;

  if (promo) {
    if (promo.discount_type === "percent") {
      discountAmount = (finalAmount * Number(promo.discount)) / 100;
    }

    if (promo.discount_type === "round") {
      discountAmount = Number(promo.discount);
    }
  }

  const totalAmount = finalAmount - discountAmount;

  if (!open || !order) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-t-3xl w-full max-w-md relative shadow-xl flex flex-col mb-16"
        style={{ maxHeight: "85vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Handle bar */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full bg-gray-200" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-3 pb-4 border-b border-gray-100">
          <h2 className="text-xl font-bold font-poppins text-gray-900">
            Order Details
          </h2>
          <button
            className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200 transition-colors"
            onClick={onClose}
            aria-label="Close"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {/* Order ID */}
          <div className="bg-gray-50 rounded-2xl px-4 py-3">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">
              Order ID
            </p>
            <p className="text-sm font-semibold text-gray-800 break-all">
              {order.order_no || "—"}
            </p>
          </div>

          {/* Status & Total Row */}
          <div className="grid grid-cols-1">
            {/* Status */}
            <div className="bg-gray-50 rounded-2xl px-4 py-3 flex flex-col gap-1">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                Status
              </p>
              <span
                className={`text-[12px] font-bold px-2 py-0.5 rounded-lg capitalize ${getStatusColor(order.status)}`}
              >
                {order.status?.replace(/_/g, " ") || "—"}
              </span>
            </div>
          </div>
          {/* Status & Total Row */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-gray-50 rounded-2xl px-4 py-3 flex flex-col gap-1">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                Subtotal
              </p>
              <p className="font-poppins text-gray-900">
                {`${finalAmount.toFixed(2)} QAR`}
              </p>
            </div>

            <div className="bg-gray-50 rounded-2xl px-4 py-3 flex flex-col gap-1">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                Discount
              </p>
              <p className="font-poppins text-gray-900">
                {discountAmount > 0
                  ? `${discountAmount.toFixed(2)} QAR`
                  : "-"}
              </p>
            </div>

            <div className="bg-gray-50 rounded-2xl px-4 py-3 flex flex-col gap-1">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                Total
              </p>
              <p className="font-poppins text-gray-900">
                {`${totalAmount.toFixed(2)} QAR`}
              </p>
            </div>
          </div>

          {/* Items */}
          <div className="bg-gray-50 rounded-2xl px-4 py-10">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
              Items
            </p>
            {garmentLines.length > 0 ? (
              <div className="space-y-1.5">
                {garmentLines.map((line, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#44B6CE] flex-shrink-0" />
                    <span className="text-sm text-gray-700">{line}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400">No items found.</p>
            )}
          </div>
        </div>

        {/* Footer — always visible */}
        {onDownloadInvoice && (
          <div className="px-6 py-10 border-t border-gray-100">
            <button
              className="w-full bg-[#44B6CE] text-white font-bold py-4 rounded-full text-base transition hover:opacity-90 active:opacity-80"
              onClick={onDownloadInvoice}
            >
              Download Invoice
            </button>
          </div>
        )}
      </div>

      <style>{`
        @keyframes slideup {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};
